// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import { Indicator } from '@mantine/core';
import type { ResourceType } from '@medplum/fhirtypes';
import { useNotificationCount } from '@medplum/react-hooks';
import type { JSX, ReactElement } from 'react';

interface SidebarNotificationIconProps {
  readonly resourceType: ResourceType;
  readonly countCriteria: string;
  readonly subscriptionCriteria: string;
  readonly iconComponent: ReactElement;
}

export function SidebarNotificationIcon({
  resourceType,
  countCriteria,
  subscriptionCriteria,
  iconComponent,
}: SidebarNotificationIconProps): JSX.Element {
  const unreadCount = useNotificationCount({ resourceType, countCriteria, subscriptionCriteria });

  if (unreadCount <= 0) {
    return iconComponent;
  }

  return (
    <Indicator inline label={unreadCount.toLocaleString()} size={16} offset={2} position="top-end" color="red">
      {iconComponent}
    </Indicator>
  );
}

interface SidebarCountIconProps {
  readonly count: number;
  readonly iconComponent: ReactElement;
}

/**
 * Indicator wrapper that takes an explicit `count` — used when the count is
 * computed by a custom hook (e.g. unread message threads) rather than a
 * server-side _summary=count query.
 */
export function SidebarCountIcon({ count, iconComponent }: SidebarCountIconProps): JSX.Element {
  if (count <= 0) {
    return iconComponent;
  }

  return (
    <Indicator inline label={count.toLocaleString()} size={16} offset={2} position="top-end" color="red">
      {iconComponent}
    </Indicator>
  );
}
